import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

/**
 * Multi-select dropdown (global Popover) listing the heads the policy allows
 * to be company arranged for the selected tier. Checked = company arranges it
 * (employee cannot claim it); unchecked = employee pays and claims it.
 */
export const CompanyArrangedSelect = ({
  heads = [],
  selections = {},
  onChange,
  loading = false,
  error = '',
  disabled = false,
}) => {
  const idOf = (h) => String(h.headId ?? h.expenseId);
  const selectedCount = heads.filter((h) => selections[idOf(h)] === true).length;

  let summary = 'Select...';
  if (loading) summary = 'Loading...';
  else if (error) summary = 'Unavailable';
  else if (heads.length === 0) summary = 'None available for this tier';
  else if (selectedCount === 0) summary = 'None (I will claim all)';
  else summary = `${selectedCount} of ${heads.length} company arranged`;

  const isDisabled = disabled || loading || !!error || heads.length === 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isDisabled}
          className={cn(
            'flex h-11 w-full items-center justify-between rounded-xl border border-[var(--color-text-border)] bg-white px-3 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 disabled:cursor-not-allowed disabled:opacity-50',
            selectedCount === 0 && 'text-muted-foreground'
          )}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px] max-h-64 overflow-y-auto p-1"
      >
        {heads.map((head) => {
          const id = idOf(head);
          const checked = selections[id] === true;
          return (
            <label
              key={id}
              className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent/10"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onChange(id, !checked)}
                className="h-4 w-4 accent-[var(--color-primary)]"
              />
              <span className="flex-1 text-foreground">{head.headName}</span>
              <span className="text-[10px] font-bold uppercase text-[var(--color-text-secondary)]">
                {checked ? 'Company' : 'Self'}
              </span>
            </label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
